import Link from "next/link";
import { cn } from "@/libs/utils";
import { getGuideHref } from "@/libs/games/game-guide";
import { BhapticsContent } from "@/store/reducers/contents";
import ContentsService from "@/services/ContentsService";
import { buttonVariants } from "@/v2-components/ui/button";
import ExternalLinkIcon from "@/v2-components/core/icon/external-link-icon";
import { useTrans } from "@/utils/useTrans";

const OVERWOLF_PLUGIN_URL =
  "https://www.overwolf.com/app/bhaptics_inc.-bhaptics_link";

/**
 * 상세 우측 aside 최상단의 가이드 버튼 (#5474, 시안 `30035:23140` `Btns`).
 *
 * ## 시안 값
 *
 * ```
 * Btns          gap 8 · 폭 424(aside 464 − pl 40)
 * How to Play   h54 · r999 · bg #0F62FE(blue-60) · 16/22 SemiBold white
 * Install …     h54 · r999 · bg #21272A(coolgray-90) · + 외부링크 아이콘 20
 * ```
 *
 * `buttonVariants` 의 `size: "md"` 가 `min-h-[54px]` + `text-lg`(16/22)라 시안과 같다 —
 * 임의 px 를 쓰지 않는다.
 *
 * ## ★종전 위치와 게이트
 *
 * `GameMetadataSection` 의 `DesktopMetadataButtons` 가 **히어로 안에서 가로로**
 * (`mt-10 hidden gap-2 laptop:flex`) 그렸다. 시안은 aside 안 세로 2개다.
 *
 * 게이트도 바뀐다. 종전은 `supportType === Mod` 였는데 **가이드가 있으면** 으로 넓혔다.
 * 근거는 prod 실측(2026-09-01) — 가이드 문서를 가진 게임 158건 중 **23건이 비-Mod** 라
 * 종전 게이트로는 버튼이 안 뜨고, 그 23건은 「게임 정보」 카드 안 iframe 이 유일한
 * 가이드 경로였다. 그 iframe 을 걷으면서(시안에 없다) 경로를 버튼으로 옮긴다.
 *
 * ⚠️ Overwolf 버튼의 게이트(`overwolfSupported`)는 **건드리지 않았다** — 가이드 유무와
 * 무관한 별개 조건이다.
 */
export default function GameGuideButtons({ game }: { game: BhapticsContent }) {
  const { t } = useTrans(["games"]);
  const guideHref = getGuideHref(game);
  const isOverwolf = ContentsService.overwolfSupported(game.supportedPlatforms);

  if (!guideHref && !isOverwolf) return null;

  return (
    /**
     * 시안 `30035:23140` — 버튼 사이 8. 카드와의 간격 32 는 호출처(aside)가 준다.
     *
     * ⚠️ **데스크탑 전용**(`hidden laptop:flex`)이 여기 있다. 모바일은 히어로의
     * `MobileMetadataButtons` 가 그리므로 둘 다 그리면 버튼이 두 번 나온다.
     * 이 처리를 호출처의 wrapper 로 올리면, 버튼이 없을 때 빈 div 가 flex 자식으로
     * 남아 카드 위에 갭만 생긴다 — 그래서 컴포넌트 안에 둔다.
     */
    <div className="hidden flex-col gap-2 laptop:flex">
      {guideHref && (
        <Link
          href={guideHref}
          target="_blank"
          className={cn(
            buttonVariants({ variant: "blue-fill", shape: "pill" }),
            "w-full",
          )}
        >
          {t("How to Play")}
        </Link>
      )}
      {isOverwolf && (
        <Link
          href={OVERWOLF_PLUGIN_URL}
          target="_blank"
          className={cn(
            buttonVariants({ variant: "darkgray-fill", shape: "pill" }),
            "w-full gap-2",
          )}
        >
          <span>{t("Install Overwolf Plugin")}</span>
          <ExternalLinkIcon className="h-5 w-5" />
        </Link>
      )}
    </div>
  );
}
